import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useId,
} from 'react';
import type {
  DialogProps as AriaDialogProps,
  ModalOverlayProps as AriaModalOverlayProps,
} from 'react-aria-components';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  Text as AriaText,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { cx } from '@/utils/cx.ts';
import { isThirdPartyEditorUi } from '@/utils/third-party-editor-ui';

export const DialogTrigger = AriaDialogTrigger;

/**
 * Context exposing the wrapping AriaModal's animation state to any descendant
 * Dialog. Used by Dialog to derive the e2e ready-marker's `data-ready`
 * attribute. Defaults to a steady (not animating) state so Dialogs rendered
 * outside of a Modal still produce a sensible marker.
 *
 * NOTE: Exported so `confirmation-dialog.tsx` can wire the same provider
 * around its inline `AriaModal` usage. External callers should not consume.
 */
/* eslint-disable-next-line react-refresh/only-export-components -- standard context pattern: context co-located with its provider */
export const ModalStateContext = createContext<{
  isEntering: boolean;
  isExiting: boolean;
}>({ isEntering: false, isExiting: false });

// =============================================================================
// Layout class names — hoisted to module scope so they aren't reallocated on
// every render. Tweak here if the modal's vertical rhythm needs to change.
// =============================================================================

const HEADER_CLASS = 'shrink-0 px-6 pt-6 pb-4';
const BODY_CLASS = 'flex-1 min-h-0 overflow-y-auto px-6';
const FOOTER_DEFAULT_CLASS = 'shrink-0 px-6 pt-4 pb-6';
const FOOTER_UNPADDED_CLASS = 'shrink-0';

const TITLE_CLASS = 'text-lg font-semibold text-fg-primary';
const DESCRIPTION_CLASS = 'mt-1 text-sm text-fg-secondary';

const HEADER_ROW_CLASS = 'flex items-start gap-4';
const HEADER_TEXT_COL_CLASS = 'flex-1 min-w-0';

const DIALOG_BASE_CLASS =
  'relative flex min-h-0 w-full flex-1 flex-col outline-hidden';

const CLOSE_BUTTON_CLASS = 'absolute top-3 right-3 z-10';

// =============================================================================
// ModalOverlay / Modal — thin wrappers around react-aria primitives that
// stamp the standard positioning, sizing, and animation classes.
// =============================================================================

export const ModalOverlay = ({
  shouldCloseOnInteractOutside: callerShouldClose,
  ...props
}: AriaModalOverlayProps) => {
  const zIndex = useStackingZIndex();
  // Never dismiss on a click into UI injected by a writing extension
  // (Grammarly) — it renders outside our React root, so react-aria sees it as
  // an outside interaction and would close the modal mid-edit, losing whatever
  // the user had typed. Composed with any caller-provided predicate so a
  // consumer passing its own rule (e.g. CardFieldsPopover's `[data-trigger]`
  // check) still has it honoured: a click dismisses only when both agree.
  const shouldCloseOnInteractOutside = useCallback(
    (target: Element) => {
      if (isThirdPartyEditorUi(target)) {
        return false;
      }
      return callerShouldClose ? callerShouldClose(target) : true;
    },
    [callerShouldClose]
  );
  return (
    <AriaModalOverlay
      {...props}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
      className={state =>
        cx(
          'fixed inset-0 flex items-end justify-center bg-black/50 px-0 pb-0 pt-0 outline-hidden sm:items-start sm:px-4 sm:pb-8 sm:pt-24',
          state.isEntering && 'duration-200 ease-out animate-in fade-in',
          state.isExiting && 'duration-200 ease-in animate-out fade-out',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
      style={{ ...props.style, zIndex }}
    />
  );
};

export const Modal = ({ children, ...props }: AriaModalOverlayProps) => {
  const zIndex = useStackingZIndex(10); // Slightly above overlay
  return (
    <AriaModal
      {...props}
      className={state =>
        cx(
          'flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-2xl bg-bg-primary shadow-2xl outline-hidden sm:max-h-[calc(100vh-12rem)] sm:rounded-2xl',
          state.isEntering &&
            'duration-200 ease-out animate-in zoom-in-95 slide-in-from-bottom-4',
          state.isExiting && 'duration-200 ease-in animate-out zoom-out-95',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
      style={{ ...props.style, zIndex }}
    >
      {state => (
        <ModalStateContext.Provider
          value={{ isEntering: state.isEntering, isExiting: state.isExiting }}
        >
          {typeof children === 'function' ? children(state) : children}
        </ModalStateContext.Provider>
      )}
    </AriaModal>
  );
};

// =============================================================================
// Dialog — owns ALL of the modal's internal layout.
//
// Consumers configure content via props (title, description, headerIcon,
// footer) and pass body content as `children`. They never render their own
// header / heading / footer / scroll wrappers. Dialog renders:
//
//   ┌──────────────────────────────────────┐
//   │ headerIcon │  Heading                │   ← shrink-0 (px-6 pt-6 pb-4)
//   │            │  Text (description)     │
//   ├──────────────────────────────────────┤
//   │  children                            │   ← flex-1 min-h-0 overflow-y-auto
//   │  ...                                 │       (px-6)
//   ├──────────────────────────────────────┤
//   │  footer                              │   ← shrink-0 (px-6 pt-4 pb-6)
//   └──────────────────────────────────────┘
//
// The header region is omitted entirely when none of title / description /
// headerIcon are set. The footer region is omitted when `footer` is unset.
// The body region is always rendered — `children` is required.
//
// PADDING CONTRACT — read this before omitting `footer`:
//
//   The body region carries HORIZONTAL padding only (`px-6`). The modal's
//   bottom padding comes entirely from the footer region (`pt-4 pb-6`). So
//   when you omit `footer`, that region is not rendered and your content
//   sits flush against the bottom edge of the card.
//
//   • Have action buttons? Put them in `footer`. That is what the slot is
//     for, it is what most consumers do, and you get the bottom padding for
//     free. Do NOT render action buttons as the last child of the body.
//   • Genuinely no footer? Add bottom padding to your own body wrapper
//     (`pb-6` to match the footer region's value). See HelpModal for the
//     established shape.
//
//   Getting this wrong looks like a broken modal, not a padding nit — the
//   email-preview modal shipped with its retry button welded to the card
//   edge because `footer` reads as optional in the props table below.
// =============================================================================

interface DialogProps extends Omit<AriaDialogProps, 'children'> {
  /** Body content. Always rendered inside the scrollable body region. */
  children: ReactNode;
  /** Modal title. Rendered inside an accessible <Heading slot="title">. */
  title?: string;
  /** Modal description. Rendered inside an accessible <Text slot="description">. */
  description?: string;
  /**
   * Optional icon (or other small visual) to render to the left of the title.
   * Consumers pass JSX for the icon; Dialog positions it. Use sparingly —
   * most modals don't need this slot.
   */
  headerIcon?: ReactNode;
  /**
   * Optional sticky footer content (typically action buttons).
   *
   * NOTE: this region supplies the modal's bottom padding. If you omit it, add
   * `pb-6` to your own body wrapper — see the PADDING CONTRACT above.
   */
  footer?: ReactNode;
  /** Hide the built-in close button (e.g. when the consumer renders its own). */
  hideCloseButton?: boolean;
  /** Disable the close button (e.g. during in-flight operations). */
  isCloseDisabled?: boolean;
  /**
   * Suppress Dialog's default footer padding. Use only when the footer
   * content brings its own padding + border (e.g. a self-contained
   * SummaryFooter component).
   */
  unpaddedFooter?: boolean;
  /**
   * Optional semantic instance name (e.g. "create-contact", "edit-deal").
   * When supplied, the Dialog renders `data-instance={instance}` for e2e
   * selectors. Format: `<action>-<entity>` (kebab-case).
   */
  instance?: string;
  /**
   * Override the default `data-component="modal"` emitted on the dialog
   * root. Used internally by `ConfirmationDialog` to stamp
   * `data-component="confirmation-dialog"`. External callers should not
   * use this — pass `instance` instead.
   */
  'data-component'?: string;
}

export const Dialog = ({
  children,
  title,
  description,
  headerIcon,
  footer,
  hideCloseButton,
  isCloseDisabled,
  unpaddedFooter,
  className,
  instance,
  ...props
}: DialogProps) => {
  // Stable ID for the description element so we can wire aria-describedby
  // explicitly. Generated unconditionally (hooks must run in the same order
  // every render) and only consumed when a description is present.
  const descriptionId = useId();
  const modalState = useContext(ModalStateContext);
  const isReady = !modalState.isEntering && !modalState.isExiting;

  const hasHeader =
    title !== undefined ||
    description !== undefined ||
    headerIcon !== undefined;

  return (
    <AriaDialog
      data-component='modal'
      data-instance={instance}
      {...props}
      className={cx(DIALOG_BASE_CLASS, className)}
      aria-describedby={
        description !== undefined
          ? [props['aria-describedby'], descriptionId]
              .filter(Boolean)
              .join(' ') || undefined
          : props['aria-describedby']
      }
    >
      {({ close }) => (
        <StackingLayer>
          {/* Hidden marker for E2E tests to detect transition completion. */}
          <div
            data-component='modal-ready-marker'
            aria-hidden='true'
            style={{ display: 'none' }}
            data-ready={isReady ? '' : undefined}
          />
          {!hideCloseButton && (
            <CloseButton
              onPress={close}
              theme='light'
              size='lg'
              className={CLOSE_BUTTON_CLASS}
              isDisabled={isCloseDisabled}
            />
          )}
          {hasHeader && (
            <div className={HEADER_CLASS}>
              <div className={HEADER_ROW_CLASS}>
                {headerIcon}
                <div className={HEADER_TEXT_COL_CLASS}>
                  {title !== undefined && (
                    <AriaHeading slot='title' className={TITLE_CLASS}>
                      {title}
                    </AriaHeading>
                  )}
                  {description !== undefined && (
                    <AriaText
                      slot='description'
                      id={descriptionId}
                      className={DESCRIPTION_CLASS}
                    >
                      {description}
                    </AriaText>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className={BODY_CLASS}>{children}</div>
          {footer != null && footer !== false && (
            <div
              className={
                unpaddedFooter ? FOOTER_UNPADDED_CLASS : FOOTER_DEFAULT_CLASS
              }
            >
              {footer}
            </div>
          )}
        </StackingLayer>
      )}
    </AriaDialog>
  );
};
